import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, isToday, isTomorrow, isPast, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';

interface DateCellProps {
  value: any;
  config?: { includeTime?: boolean; timezone?: string };
  onChange?: (value: string) => void;
  readOnly?: boolean;
}

export const DateCell: React.FC<DateCellProps> = ({ value, config, onChange, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);

  let date: Date | null = null;
  try {
    if (value) {
      let tempDate: Date;
      if (typeof value === 'string') {
        const parsed = JSON.parse(value);
        tempDate = new Date(parsed.date || parsed);
      } else if (value.date) {
        tempDate = new Date(value.date);
      } else {
        tempDate = new Date(value);
      }
      // Validar se a data é válida
      if (!isNaN(tempDate.getTime())) {
        date = tempDate;
      }
    }
  } catch (e) {
    // Keep null
  }

  const handleSelect = (selectedDate: Date | undefined) => {
    if (onChange && !readOnly && selectedDate) {
      onChange(JSON.stringify({ date: selectedDate.toISOString() }));
    }
    setIsOpen(false);
  };

  const getRelativeText = (d: Date) => {
    if (isToday(d)) return 'Hoje';
    if (isTomorrow(d)) return 'Amanhã';
    const days = differenceInDays(d, new Date());
    if (days > 0 && days <= 7) return `Em ${days} dias`;
    if (days < 0 && days >= -7) return `${Math.abs(days)} dias atrás`;
    return format(d, 'dd/MM/yyyy', { locale: ptBR });
  };

  const getStatusColor = (d: Date) => {
    if (isPast(d) && !isToday(d)) return 'bg-red-100 text-red-700 border-red-300';
    if (isToday(d) || isTomorrow(d)) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (!date) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            disabled={readOnly}
            className="w-full px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-50 rounded flex items-center gap-2 disabled:cursor-not-allowed"
          >
            <CalendarIcon className="w-4 h-4" />
            <span>-</span>
          </button>
        </PopoverTrigger>
        {!readOnly && (
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={undefined} onSelect={handleSelect} initialFocus />
          </PopoverContent>
        )}
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={readOnly}
          className={`w-full px-3 py-1.5 text-sm rounded border font-medium transition-all hover:opacity-80 disabled:cursor-not-allowed ${getStatusColor(
            date
          )}`}
        >
          {getRelativeText(date)}
        </button>
      </PopoverTrigger>
      {!readOnly && (
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} onSelect={handleSelect} initialFocus />
        </PopoverContent>
      )}
    </Popover>
  );
};
